import axios, { AxiosResponse } from 'axios';
import { axiosInstance } from './clients/axios';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { normalizeAssetUploadError, prepareAssetUploadFile } from './assetuploadpreparation';

type UploadableAssetFile = File & {
  id?: string;
  url?: string;
};

interface UploadAssetParams {
  file: UploadableAssetFile;
  onError?: (error: Error) => void;
  onSuccess?: (file: UploadableAssetFile) => void;
  source?: string;
  usage: string;
}

export class AssetUploaderClient {
  async uploadFile({ file, onError, onSuccess, usage, source }: UploadAssetParams) {
    try {
      if (!source) source = 'ASSET';
      const uploadFile = (await prepareAssetUploadFile(file)) as UploadableAssetFile;

      const uploadRequestInfo = await this.initFileUpload(uploadFile, usage, source);
      await this.uploadFileToCloudflare(uploadRequestInfo.url, usage, uploadFile);
      const uploadedAssetInfo = await this.finishFileUpload(uploadRequestInfo.path);
      uploadFile.url = uploadedAssetInfo.url;
      uploadFile.id = uploadedAssetInfo.id;
      onSuccess?.(uploadFile);
    } catch (error: unknown) {
      onError?.(normalizeAssetUploadError(error));
    }
  }

  private async initFileUpload(file: UploadableAssetFile, usage: string, source?: string) {
    const payload = {
      usage: usage,
      type: file.type,
      name: file.name,
      source: source ? source : 'ASSET',
    };
    const response: AxiosResponse = await axiosInstance.post(
      `${GATEWAY_API_URL}/asset/upload/init`,
      payload,
    );
    return response.data?.data;
  }

  private async uploadFileToCloudflare(url: string, usage: string, file: UploadableAssetFile) {
    const axios_instance = axios.create();

    let response: AxiosResponse | undefined = undefined;

    if (usage === 'VIDEO') {
      const formData = new FormData();
      formData.append('file', file);

      response = await axios_instance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      response = await axios_instance.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });
    }
    return response;
  }

  private async finishFileUpload(path: string) {
    const payload = { path: path };
    const response: AxiosResponse = await axiosInstance.post(
      `${GATEWAY_API_URL}/asset/upload/finish`,
      payload,
    );
    return response.data?.data;
  }
}
